export type LoginIframeOptions = {
  variant?: 'client' | 'staff';
  /** Full HTTPS return URL for failed-login redirect (allowlisted). */
  returnUrl?: string;
  /** Path-only return when returnUrl omitted — combined with site origin. */
  returnPath?: string;
  /** Regional site hostname (data-pq-domain); see `default.ts` `sites`. */
  siteDomain?: string;
  embedBase?: string;
  minHeight?: number;
  lostPasswordPath?: string;
  registerPath?: string;
  /** Submit button label (data-pq-submit-label). Defaults by variant if omitted. */
  submitLabel?: string;
  /**
   * Hide the Demo button (client variant only; staff already omits it).
   * Set `data-pq-hide-demo="true"` on the host when the site has no `/demo` page.
   */
  hideDemo?: boolean;
};

const DEFAULT_CLIENT_SUBMIT_LABEL = 'Login to PQ Online';
const DEFAULT_STAFF_SUBMIT_LABEL = 'Login To Staff Portal';

const RESIZE_MESSAGE = 'pq-login-resize';
const LAYOUT_MESSAGE = 'pq-login-layout';
const REQUEST_HEIGHT_MESSAGE = 'pq-login-request-height';
/** Matches inline Sitebuilder login block height until postMessage resize runs. */
const DEFAULT_MIN_HEIGHT = 375;

function readHostWidth(host: HTMLElement): number {
  const width = Math.round(host.getBoundingClientRect().width);
  if (width > 0) {
    return width;
  }
  const parentWidth = host.parentElement
    ? Math.round(host.parentElement.getBoundingClientRect().width)
    : 0;
  return parentWidth > 0 ? parentWidth : 0;
}

function postLayoutWidth(iframe: HTMLIFrameElement, width: number): void {
  if (width <= 0 || !iframe.contentWindow) {
    return;
  }
  iframe.contentWindow.postMessage({ type: LAYOUT_MESSAGE, width }, '*');
}

/** Captured synchronously while the script tag runs — null after DOMContentLoaded. */
const LOADER_SCRIPT_ORIGIN = ((): string => {
  const script = document.currentScript;
  if (!script?.src) {
    return '';
  }
  try {
    return new URL(script.src).origin;
  } catch {
    return '';
  }
})();

function resolveEmbedBase(explicit?: string): string {
  const fromOption = explicit?.trim().replace(/\/+$/, '');
  if (fromOption) {
    return fromOption;
  }
  const fromWindow = window.__PQ_FORMS_PROXY_URL__?.trim().replace(/\/+$/, '');
  if (fromWindow) {
    return fromWindow;
  }
  if (LOADER_SCRIPT_ORIGIN) {
    return LOADER_SCRIPT_ORIGIN;
  }
  return '';
}

function normalizeVariant(raw: string | null | undefined): 'client' | 'staff' {
  const value = (raw ?? 'client').trim().toLowerCase();
  if (value === 'staff' || value === 'resource') {
    return 'staff';
  }
  return 'client';
}

function normalizeSiteOrigin(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) {
    return null;
  }
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
}

function resolveSiteOrigin(
  siteDomain: string | null | undefined,
  fallbackOrigin: string = window.location.origin,
): string {
  return normalizeSiteOrigin(siteDomain) ?? fallbackOrigin;
}

function resolveSubmitLabel(
  variant: 'client' | 'staff',
  explicit?: string | null,
): string {
  const custom = explicit?.trim();
  if (custom) {
    return custom;
  }
  return variant === 'staff' ? DEFAULT_STAFF_SUBMIT_LABEL : DEFAULT_CLIENT_SUBMIT_LABEL;
}

function truthyAttr(value: string | null | undefined): boolean {
  if (value == null) {
    return false;
  }
  const v = value.trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === '';
}

function resolveReturnUrl(
  returnUrl: string | null | undefined,
  returnPath: string | null | undefined,
  variant: 'client' | 'staff',
  siteOrigin: string,
): string {
  const full = returnUrl?.trim();
  if (full) {
    return full;
  }
  const path =
    returnPath?.trim() || (variant === 'staff' ? '/staff-login' : '/login');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${siteOrigin}${normalized}`;
}

function mount(host: HTMLElement, options: LoginIframeOptions = {}): HTMLIFrameElement {
  const variant = normalizeVariant(
    options.variant ?? host.getAttribute('data-pq-login-variant') ?? undefined,
  );
  const siteOrigin = resolveSiteOrigin(
    options.siteDomain ?? host.getAttribute('data-pq-domain') ?? undefined,
  );
  const returnUrl = resolveReturnUrl(
    options.returnUrl ?? host.getAttribute('data-pq-return-url') ?? undefined,
    options.returnPath ?? host.getAttribute('data-pq-return') ?? undefined,
    variant,
    siteOrigin,
  );
  const embedBase = resolveEmbedBase(options.embedBase);
  if (!embedBase) {
    throw new Error(
      'PQLoginIframe: set data-pq-embed-base on the host, window.__PQ_FORMS_PROXY_URL__, or load login-iframe.js from the proxy origin.',
    );
  }
  const minHeight = options.minHeight ?? DEFAULT_MIN_HEIGHT;

  host.style.width = '100%';
  const maxWidth = host.getAttribute('data-pq-max-width')?.trim();
  host.style.maxWidth = maxWidth || '100%';
  host.style.marginLeft = 'auto';
  host.style.marginRight = 'auto';
  host.style.boxSizing = 'border-box';
  host.style.minWidth = '0';

  const layoutWidth = readHostWidth(host);

  const lostPassword =
    options.lostPasswordPath?.trim() ||
    host.getAttribute('data-pq-lost-password')?.trim() ||
    (variant === 'staff' ? '/login/staff/lost-password' : '/login/lost-password');
  const register =
    options.registerPath?.trim() ||
    host.getAttribute('data-pq-register')?.trim() ||
    '/login/pq-online/register';

  const submitLabel = resolveSubmitLabel(
    variant,
    options.submitLabel ?? host.getAttribute('data-pq-submit-label'),
  );
  const hideDemo =
    options.hideDemo === true ||
    truthyAttr(host.getAttribute('data-pq-hide-demo'));

  const params = new URLSearchParams({ variant, return_url: returnUrl, site_domain: siteOrigin });
  params.set('submit_label', submitLabel);
  if (lostPassword) {
    params.set('lost_password', lostPassword.replace(/^\//, ''));
  }
  if (register) {
    params.set('register', register.replace(/^\//, ''));
  }
  if (hideDemo) {
    params.set('hide_demo', '1');
  }
  if (layoutWidth > 0) {
    params.set('layout_width', String(layoutWidth));
  }

  const iframe = document.createElement('iframe');
  iframe.title = variant === 'staff' ? 'Resource Centre login' : 'PQ Online login';
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  iframe.style.width = '100%';
  iframe.style.maxWidth = '100%';
  iframe.style.border = '0';
  iframe.style.display = 'block';
  iframe.style.minHeight = `${String(minHeight)}px`;
  iframe.style.overflow = 'hidden';
  iframe.setAttribute('scrolling', 'no');

  host.replaceChildren(iframe);
  iframe.dataset.pqLoginSrc = `${embedBase}/embed/login.html?${params.toString()}`;
  return iframe;
}

function applyIframeHeight(
  host: HTMLElement,
  iframe: HTMLIFrameElement,
  height: number,
  fallback: number,
): void {
  const measured = Math.ceil(height);
  const next = measured > 0 ? measured + 2 : fallback;
  iframe.style.height = `${String(next)}px`;
  iframe.style.minHeight = '0';
  host.style.height = `${String(next)}px`;
}

function requestEmbedHeight(iframe: HTMLIFrameElement): void {
  iframe.contentWindow?.postMessage({ type: REQUEST_HEIGHT_MESSAGE }, '*');
}

function wireLayout(host: HTMLElement, iframe: HTMLIFrameElement): () => void {
  const sync = (): void => {
    postLayoutWidth(iframe, readHostWidth(host));
  };

  iframe.addEventListener('load', sync);

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(sync);
    observer.observe(host);
    return () => {
      iframe.removeEventListener('load', sync);
      observer.disconnect();
    };
  }

  window.addEventListener('resize', sync);
  return () => {
    iframe.removeEventListener('load', sync);
    window.removeEventListener('resize', sync);
  };
}

function wireResize(host: HTMLElement, iframe: HTMLIFrameElement, minHeight: number): void {
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow) {
      return;
    }
    const data = event.data as { type?: string; height?: number } | null;
    if (!data || data.type !== RESIZE_MESSAGE || typeof data.height !== 'number') {
      return;
    }
    applyIframeHeight(host, iframe, data.height, minHeight);
  });

  const syncHeight = (): void => {
    postLayoutWidth(iframe, readHostWidth(host));
    requestEmbedHeight(iframe);
  };

  iframe.addEventListener('load', syncHeight);
  for (const delayMs of [50, 150, 300, 600, 1200, 2500]) {
    window.setTimeout(syncHeight, delayMs);
  }
}

function mountLoginIframe(host: HTMLElement, options: LoginIframeOptions = {}): HTMLIFrameElement {
  const minHeight =
    options.minHeight ??
    (Number(host.getAttribute('data-pq-min-height') ?? String(DEFAULT_MIN_HEIGHT)) ||
      DEFAULT_MIN_HEIGHT);
  const iframe = mount(host, { ...options, minHeight });
  wireLayout(host, iframe);
  wireResize(host, iframe, minHeight);
  const src = iframe.dataset.pqLoginSrc;
  if (src) {
    iframe.src = src;
  }
  return iframe;
}

function autoInit(): void {
  document.querySelectorAll<HTMLElement>('[data-pq-login-iframe]').forEach((host) => {
    if (host.dataset.pqLoginIframeWired === '1') {
      return;
    }
    host.dataset.pqLoginIframeWired = '1';
    try {
      mountLoginIframe(host, {
        variant: normalizeVariant(host.getAttribute('data-pq-login-variant')),
        returnUrl: host.getAttribute('data-pq-return-url') ?? undefined,
        returnPath: host.getAttribute('data-pq-return') ?? undefined,
        siteDomain: host.getAttribute('data-pq-domain') ?? undefined,
        embedBase: host.getAttribute('data-pq-embed-base') ?? undefined,
        lostPasswordPath: host.getAttribute('data-pq-lost-password') ?? undefined,
        registerPath: host.getAttribute('data-pq-register') ?? undefined,
        submitLabel: host.getAttribute('data-pq-submit-label') ?? undefined,
        hideDemo: truthyAttr(host.getAttribute('data-pq-hide-demo')),
      });
    } catch (err) {
      console.error(err);
    }
  });
}

export type PQLoginIframeApi = {
  mount: typeof mountLoginIframe;
};

declare global {
  interface Window {
    PQLoginIframe?: PQLoginIframeApi;
    __PQ_FORMS_PROXY_URL__?: string;
  }
}

window.PQLoginIframe = { mount: mountLoginIframe };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}
